import React, { useState, useRef } from "react";
import { Box, Typography, TextField, Button, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImage } from "../../firebase/storage";

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  placeholder?: string;
  description?: string;
}

export const ImageUpload = ({
  value,
  onChange,
  label = "Image",
  placeholder = "Enter URL or upload image",
  description,
}: ImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Allowed file types and max size
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Invalid file type. Allowed: JPG, PNG, GIF, WebP, SVG");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError("File too large. Maximum size is 5MB");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Upload to Firebase Storage
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        {label}
      </Typography>

      {/* Preview */}
      {value && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 128,
            bgcolor: "grey.100",
            borderRadius: 2,
            overflow: "hidden",
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={value}
            alt="Preview"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text fill="%239ca3af" font-size="12" x="50%" y="50%" text-anchor="middle" dy=".3em">Invalid URL</text></svg>';
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <IconButton
            onClick={() => onChange("")}
            size="small"
            title="Remove image"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "error.main",
              color: "white",
              "&:hover": {
                bgcolor: "error.dark",
              },
              width: 28,
              height: 28,
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* URL Input */}
      <TextField
        fullWidth
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
      />

      {/* Upload Button */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <Button
          fullWidth
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          variant="outlined"
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            bgcolor: "grey.100",
            borderColor: "grey.300",
            color: "text.primary",
            "&:hover": {
              bgcolor: "grey.200",
              borderColor: "grey.400",
            },
          }}
        >
          {isUploading ? "Uploading..." : "Upload Image"}
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      {/* Help text */}
      <Typography variant="caption" color="text.secondary">
        Max 5MB. Supports JPG, PNG, GIF, WebP, SVG
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
};
